import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  getEvents as getArchivedEvents,
  getMarineSpeciesChecklist,
  getMarineWildlifeContext,
  getUpcomingEvents,
} from '../../api';
import {
  buildThemeEventPayload,
  DISCOVERY_THEME_ORDER,
  buildThemeHref,
  filterEventsForTheme,
  getCuratedEventsForTheme,
  getDiscoveryTheme,
  getThemeMenuItems,
  getThemeRequestFilters,
  readDiscoveryTheme,
} from '../../lib/discoveryThemes';
import {
  buildArchiveEventPayload,
  buildHomeLeadFallback,
  buildThemeCountdowns,
  formatAtlasDate,
  formatCoordinate,
  HOME_FALLBACK_LOCATION,
  resolveShowcaseCoordinate,
} from '../../lib/homeLanding';
import SignalGlobe from './SignalGlobe';
import { ThemeSwitch } from '../Theme/ThemeSwitch';

function withTimeout(promise, timeoutMs = 4500) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Timed out while loading Tellus home briefing.')), timeoutMs);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

function buildTrackDescription(themeConfig, itemKey) {
  if (itemKey === 'planner') {
    return 'Set the place, date, and search radius first, then let Tellus narrow the window to what is most worth watching.';
  }

  if (itemKey === 'events') {
    return 'Scan the dated signals first when the question is what is on the calendar and what is about to crest.';
  }

  if (itemKey === 'catalog') {
    if (themeConfig.key === 'wildlife') {
      return 'Work backward from the species when you already know the animal, taxon, or field cue you care about most.';
    }

    return `Open the ${themeConfig.catalog.title.toLowerCase()} when you want slower background context, not just the next scheduled signal.`;
  }

  return 'Read the framing for this mode and how Tellus is choosing what to foreground for the region in view.';
}

function buildFeaturedTracks(themeConfig, location) {
  return getThemeMenuItems(themeConfig.key, location.search).map((item) => ({
    key: item.key,
    title: item.label,
    description: buildTrackDescription(themeConfig, item.key),
    href: item.to,
  }));
}

function describeRelativeWindow(value, referenceDate = new Date()) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Seasonal marker';

  const diffMs = parsed.getTime() - referenceDate.getTime();
  const diffHours = Math.round(diffMs / 36e5);
  const diffDays = Math.floor(diffMs / 864e5);

  if (Math.abs(diffHours) < 24) {
    return diffHours >= 0 ? `in ${Math.max(diffHours, 1)}h` : `${Math.abs(diffHours)}h ago`;
  }

  if (Math.abs(diffDays) < 45) {
    return diffDays >= 0 ? `in ${Math.max(diffDays, 1)}d` : `${Math.abs(diffDays)}d ago`;
  }

  const diffWeeks = Math.round(diffDays / 7);
  return diffWeeks >= 0 ? `in ${Math.max(diffWeeks, 1)}w` : `${Math.abs(diffWeeks)}w ago`;
}

function formatShowcaseDateParts(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { day: '--', month: 'Date TBD', detail: 'Timing TBD' };
  }

  return {
    day: parsed.toLocaleDateString(undefined, { day: '2-digit' }),
    month: parsed.toLocaleDateString(undefined, { month: 'short' }),
    detail: parsed.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
}

function buildFallbackShowcaseEntry(theme, referenceDate = new Date()) {
  const fallback = buildHomeLeadFallback(theme, referenceDate);
  const countdownFallback = buildThemeCountdowns(theme, referenceDate)[0];
  const resolvedStartsAt =
    fallback.starts_at && !Number.isNaN(new Date(fallback.starts_at).getTime())
      ? fallback.starts_at
      : countdownFallback?.isoDate || '';
  const themeConfig = getDiscoveryTheme(theme);
  const dateParts = formatShowcaseDateParts(resolvedStartsAt);
  const coordinate = resolveShowcaseCoordinate(fallback, theme);

  return {
    id: `${theme}-fallback`,
    theme,
    themeLabel: themeConfig.label,
    title: fallback.title,
    summary: fallback.summary,
    status: fallback.status || 'seasonal marker',
    startsAt: resolvedStartsAt,
    countdownLabel: describeRelativeWindow(resolvedStartsAt, referenceDate),
    formattedDate: formatAtlasDate(resolvedStartsAt),
    sourceLabel: fallback.category_label || 'Seasonal marker',
    distanceLabel: 'Regional seasonal marker',
    regionLabel: coordinate.regionLabel,
    lat: coordinate.lat,
    lon: coordinate.lon,
    ...dateParts,
  };
}

function normalizeShowcaseEntry(theme, item, referenceDate = new Date()) {
  if (!item) return buildFallbackShowcaseEntry(theme, referenceDate);

  const startsAt = item.starts_at || item.start_ts || item.start_date || '';
  const dateParts = formatShowcaseDateParts(startsAt);
  const themeConfig = getDiscoveryTheme(theme);
  const coordinate = resolveShowcaseCoordinate(item, theme);

  return {
    id: item.id || item.item_type_id || item._id || `${theme}-${item.title || 'signal'}`,
    theme,
    themeLabel: themeConfig.label,
    title: item.title || item.name || item.event_type || buildFallbackShowcaseEntry(theme, referenceDate).title,
    summary:
      item.summary ||
      item.description ||
      item.reason ||
      buildFallbackShowcaseEntry(theme, referenceDate).summary,
    status: item.status || 'scheduled',
    startsAt,
    countdownLabel: describeRelativeWindow(startsAt, referenceDate),
    formattedDate: formatAtlasDate(startsAt),
    sourceLabel: item.category_label || item.category || `${themeConfig.label} signal`,
    distanceLabel:
      item.distance_km != null ? `${item.distance_km.toFixed(1)} km from center` : 'Regional signal',
    regionLabel: coordinate.regionLabel,
    lat: coordinate.lat,
    lon: coordinate.lon,
    ...dateParts,
  };
}

function buildContextCards(activeTheme, themeConfig, checklistPayload, wildlifePayload) {
  if (activeTheme !== 'wildlife') {
    return (themeConfig.catalog.cards || []).slice(0, 3).map((item) => ({
      key: item.title,
      kicker: item.subtitle || themeConfig.label,
      title: item.title,
      summary: item.summary,
      details: [],
    }));
  }

  const strataCards = Array.isArray(checklistPayload?.strata_cards)
    ? checklistPayload.strata_cards.slice(0, 2)
    : [];
  const seasonalNotes = Array.isArray(wildlifePayload?.seasonal_context?.notes)
    ? wildlifePayload.seasonal_context.notes
    : [];
  const nearbyNotes = Array.isArray(wildlifePayload?.nearby_pattern_notes)
    ? wildlifePayload.nearby_pattern_notes
    : [];

  const cards = strataCards.map((item) => ({
    key: item.id || item.label,
    kicker: 'Likely groups',
    title: item.label || 'Wildlife stratum',
    summary: Array.isArray(item.likely_species_groups)
      ? item.likely_species_groups.join(', ')
      : 'Context pending',
    details: [
      {
        label: 'Look for',
        value: Array.isArray(item.cues) ? item.cues.join(', ') : 'No cue list yet',
      },
      {
        label: 'Timing',
        value: Array.isArray(item.timing_notes) ? item.timing_notes.join(' ') : 'No timing notes yet',
      },
    ],
  }));

  if (seasonalNotes.length || nearbyNotes.length) {
    cards.push({
      key: 'field-notes',
      kicker: 'Field notes',
      title: 'What else to watch for',
      summary: seasonalNotes[0] || nearbyNotes[0] || 'No additional field notes yet.',
      details: seasonalNotes
        .slice(1, 2)
        .concat(nearbyNotes.slice(0, 1))
        .map((note, index) => ({
          label: index === 0 ? 'Seasonal' : 'Nearby',
          value: note,
        })),
    });
  }

  return cards;
}

function buildFallbackStoryCards(theme, curatedEvents, countdowns) {
  if (theme === 'cultural' && curatedEvents.length) {
    return curatedEvents.slice(0, 3).map((item) => ({
      key: item.id,
      kicker: item.category_label || 'Curated',
      title: item.title,
      summary: item.summary,
      status: item.status || 'curated',
      startsAt: item.starts_at,
      distance: null,
    }));
  }

  return countdowns.slice(0, 3).map((item) => ({
    key: item.id,
    kicker: 'Seasonal marker',
    title: item.title,
    summary: item.summary,
    status: item.cadence,
    startsAt: item.isoDate,
    distance: null,
  }));
}

export const Home = ({ lat, lng }) => {
  const location = useLocation();
  const activeTheme = readDiscoveryTheme(location.search);
  const themeConfig = getDiscoveryTheme(activeTheme);
  const featuredTracks = buildFeaturedTracks(themeConfig, location);
  const curatedEvents = useMemo(() => getCuratedEventsForTheme(activeTheme), [activeTheme]);
  const secondaryLeadHref = buildThemeHref(
    themeConfig.home.secondaryPath || '/species',
    location.search,
    activeTheme,
    themeConfig.home.secondaryOverrides || { page: 1 }
  );
  const activeThemeFilters = useMemo(() => getThemeRequestFilters(activeTheme), [activeTheme]);

  const initialLat = Number.isFinite(lat) ? Number(lat.toFixed(4)) : HOME_FALLBACK_LOCATION.lat;
  const initialLng = Number.isFinite(lng) ? Number(lng.toFixed(4)) : HOME_FALLBACK_LOCATION.lng;

  const [draftLat, setDraftLat] = useState(initialLat);
  const [draftLng, setDraftLng] = useState(initialLng);
  const [briefingLat, setBriefingLat] = useState(initialLat);
  const [briefingLng, setBriefingLng] = useState(initialLng);
  const [days, setDays] = useState(14);
  const [radiusKm, setRadiusKm] = useState(50);
  const [briefingSource, setBriefingSource] = useState(
    Number.isFinite(lat) && Number.isFinite(lng) ? 'browser' : 'sample'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eventsPayload, setEventsPayload] = useState(null);
  const [checklistPayload, setChecklistPayload] = useState(null);
  const [wildlifePayload, setWildlifePayload] = useState(null);
  const [themeShowcaseSignals, setThemeShowcaseSignals] = useState({});
  const [orbitIndex, setOrbitIndex] = useState(0);

  useEffect(() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const nextLat = Number(lat.toFixed(4));
    const nextLng = Number(lng.toFixed(4));
    setDraftLat(nextLat);
    setDraftLng(nextLng);
    setBriefingLat(nextLat);
    setBriefingLng(nextLng);
    setBriefingSource('browser');
  }, [lat, lng]);

  useEffect(() => {
    if (!Number.isFinite(briefingLat) || !Number.isFinite(briefingLng)) {
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError('');

    const upcomingRequest = withTimeout(
      getUpcomingEvents({
        lat: briefingLat,
        lon: briefingLng,
        radiusKm,
        days,
        interests: activeThemeFilters.interests,
        categories: activeThemeFilters.categories,
      })
    )
      .then(({ data }) => ({
        data: buildThemeEventPayload(data, activeTheme),
      }))
      .catch(async () => {
        const { data } = await withTimeout(getArchivedEvents(1));
        const baseItems = data?.data || [];
        const themedItems = filterEventsForTheme(baseItems, activeTheme);
        return { data: buildArchiveEventPayload(themedItems.slice(0, 8)) };
      });

    if (activeTheme === 'wildlife') {
      Promise.allSettled([
        upcomingRequest,
        withTimeout(
          getMarineSpeciesChecklist({
            lat: briefingLat,
            lon: briefingLng,
            date: new Date().toISOString().slice(0, 10),
            intent: 'observe',
          })
        ),
        withTimeout(
          getMarineWildlifeContext({
            lat: briefingLat,
            lon: briefingLng,
            date: new Date().toISOString().slice(0, 10),
          })
        ),
      ])
        .then(([eventsResult, checklistResult, wildlifeResult]) => {
          if (!active) return;

          if (eventsResult.status === 'rejected') {
            throw eventsResult.reason;
          }

          setEventsPayload(eventsResult.value.data);
          setChecklistPayload(
            checklistResult.status === 'fulfilled' ? checklistResult.value.data : null
          );
          setWildlifePayload(
            wildlifeResult.status === 'fulfilled' ? wildlifeResult.value.data : null
          );
        })
        .catch((requestError) => {
          if (!active) return;
          setError(
            requestError?.response?.data?.error ||
              requestError?.message ||
              'Failed to load the Tellus front page right now.'
          );
        })
        .finally(() => {
          if (!active) return;
          setLoading(false);
        });
    } else {
      upcomingRequest
        .then((eventsRes) => {
          if (!active) return;
          setEventsPayload(eventsRes.data);
          setChecklistPayload(null);
          setWildlifePayload(null);
        })
        .catch((requestError) => {
          if (!active) return;
          setError(
            requestError?.response?.data?.error ||
              requestError?.message ||
              'Failed to load the Tellus front page right now.'
          );
        })
        .finally(() => {
          if (!active) return;
          setLoading(false);
        });
    }

    return () => {
      active = false;
    };
  }, [activeTheme, activeThemeFilters, briefingLat, briefingLng, curatedEvents, days, radiusKm]);

  useEffect(() => {
    if (!Number.isFinite(briefingLat) || !Number.isFinite(briefingLng)) {
      return undefined;
    }

    let active = true;

    Promise.allSettled(
      DISCOVERY_THEME_ORDER.map((theme) =>
        withTimeout(
          getUpcomingEvents({
            lat: briefingLat,
            lon: briefingLng,
            radiusKm: Math.max(radiusKm, 75),
            days: 365,
            ...getThemeRequestFilters(theme),
          }),
          4500
        )
      )
    ).then((results) => {
      if (!active) return;

      const nextSignals = {};
      const referenceDate = new Date();

      DISCOVERY_THEME_ORDER.forEach((theme, index) => {
        const result = results[index];

        if (result?.status === 'fulfilled') {
          const liveEvents = filterEventsForTheme(result.value?.data?.events || [], theme);
          nextSignals[theme] = normalizeShowcaseEntry(theme, liveEvents[0], referenceDate);
          return;
        }

        nextSignals[theme] = buildFallbackShowcaseEntry(theme, referenceDate);
      });

      setThemeShowcaseSignals(nextSignals);
    });

    return () => {
      active = false;
    };
  }, [briefingLat, briefingLng, radiusKm]);

  const countdowns = useMemo(() => buildThemeCountdowns(activeTheme, new Date()), [activeTheme]);
  const nearbyEvents = Array.isArray(eventsPayload?.events) ? eventsPayload.events.slice(0, 4) : [];
  const leadEvent = nearbyEvents[0] || buildHomeLeadFallback(activeTheme, new Date());
  const headlineQueue = nearbyEvents.slice(1, 4);
  const contextCards = buildContextCards(activeTheme, themeConfig, checklistPayload, wildlifePayload);
  const storyCards =
    nearbyEvents.length > 1
      ? nearbyEvents.slice(1, 4).map((item) => ({
          key: item.id || item.item_type_id || item.title,
          kicker: item.category_label || item.category || 'Signal',
          title: item.title,
          summary: item.summary || item.description || 'No summary available yet.',
          status: item.status || 'scheduled',
          startsAt: item.starts_at || item.start_ts,
          distance: item.distance_km,
        }))
      : buildFallbackStoryCards(activeTheme, curatedEvents, countdowns);

  const headlineItems = headlineQueue.length
    ? headlineQueue.map((item) => ({
        key: item.id || item.item_type_id || item.title,
        title: item.title,
        meta:
          item.distance_km != null
            ? `${item.distance_km.toFixed(1)} km • ${item.status || 'scheduled'}`
            : item.status || 'scheduled',
      }))
    : countdowns.slice(0, 3).map((item) => ({
        key: item.id,
        title: item.title,
        meta: `${item.countdownLabel} • ${item.formattedDate}`,
      }));

  const themeCountdownCards = useMemo(() => {
    const referenceDate = new Date();

    return DISCOVERY_THEME_ORDER.map((theme) => themeShowcaseSignals[theme] || buildFallbackShowcaseEntry(theme, referenceDate));
  }, [themeShowcaseSignals]);

  const showcaseEntries = useMemo(() => {
    const referenceDate = new Date();
    const liveEntries = themeCountdownCards;
    const seasonalEntries = DISCOVERY_THEME_ORDER.flatMap((theme) =>
      buildThemeCountdowns(theme, referenceDate).slice(0, 2).map((item) => {
        const dateParts = formatShowcaseDateParts(item.isoDate);
        const coordinate = resolveShowcaseCoordinate(item, theme);

        return {
          id: `${theme}-${item.id}`,
          theme,
          themeLabel: getDiscoveryTheme(theme).label,
          title: item.title,
          summary: item.summary,
          status: item.cadence,
          startsAt: item.isoDate,
          countdownLabel: item.countdownLabel,
          formattedDate: item.formattedDate,
          sourceLabel: item.subtitle,
          distanceLabel: 'Seasonal marker',
          regionLabel: coordinate.regionLabel,
          lat: coordinate.lat,
          lon: coordinate.lon,
          ...dateParts,
        };
      })
    );

    const deduped = [...liveEntries, ...seasonalEntries].filter(
      (item, index, collection) =>
        collection.findIndex((candidate) => candidate.theme === item.theme && candidate.title === item.title) === index
    );

    const sortedEntries = deduped.sort((left, right) => {
      const leftTime = new Date(left.startsAt).getTime();
      const rightTime = new Date(right.startsAt).getTime();

      if (Number.isNaN(leftTime) && Number.isNaN(rightTime)) return 0;
      if (Number.isNaN(leftTime)) return 1;
      if (Number.isNaN(rightTime)) return -1;
      return leftTime - rightTime;
    });

    const themedEntries = sortedEntries.filter((item) => item.theme === activeTheme);
    if (themedEntries.length) {
      return themedEntries;
    }

    const themedSeasonalEntries = seasonalEntries.filter((item) => item.theme === activeTheme);
    return themedSeasonalEntries.length ? themedSeasonalEntries : [buildFallbackShowcaseEntry(activeTheme, referenceDate)];
  }, [activeTheme, themeCountdownCards]);

  useEffect(() => {
    if (!showcaseEntries.length) return;

    const baseSeed = Math.abs(Math.round((briefingLat + briefingLng) * 10)) + new Date().getUTCMinutes();
    setOrbitIndex(baseSeed % showcaseEntries.length);
  }, [activeTheme, briefingLat, briefingLng, showcaseEntries.length]);

  useEffect(() => {
    if (!showcaseEntries.length) return undefined;

    const timer = window.setInterval(() => {
      setOrbitIndex((currentIndex) => (currentIndex + 1) % showcaseEntries.length);
    }, 4800);

    return () => window.clearInterval(timer);
  }, [showcaseEntries.length]);

  const activeShowcase = showcaseEntries[orbitIndex] || buildFallbackShowcaseEntry(activeTheme);
  const showcaseQueue = showcaseEntries.length
    ? Array.from({ length: Math.min(3, Math.max(showcaseEntries.length - 1, 0)) }, (_, offset) => {
        const nextIndex = (orbitIndex + offset + 1) % showcaseEntries.length;
        return showcaseEntries[nextIndex];
      }).filter(Boolean)
    : [];

  const briefingLocationLabel = `${formatCoordinate(briefingLat)}, ${formatCoordinate(briefingLng)}`;
  const locationNote =
    briefingSource === 'sample'
      ? `Using ${HOME_FALLBACK_LOCATION.label} until you share a location.`
      : briefingSource === 'browser'
        ? 'Using your browser location for this front page.'
        : 'Using the coordinates you supplied for this front page.';

  const applyLocationDraft = () => {
    const nextLat = Number(draftLat);
    const nextLng = Number(draftLng);

    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) {
      setError('Use valid latitude and longitude values to update the front page.');
      return;
    }

    setBriefingLat(Number(nextLat.toFixed(4)));
    setBriefingLng(Number(nextLng.toFixed(4)));
    setBriefingSource('manual');
  };

  const useBrowserLocation = () => {
    if (!navigator.geolocation) {
      setError('Browser geolocation is unavailable on this device.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextLat = Number(position.coords.latitude.toFixed(4));
        const nextLng = Number(position.coords.longitude.toFixed(4));
        setDraftLat(nextLat);
        setDraftLng(nextLng);
        setBriefingLat(nextLat);
        setBriefingLng(nextLng);
        setBriefingSource('browser');
      },
      () => {
        setError('Unable to access browser location right now.');
      }
    );
  };

  return (
    <div className="atlas-home">
      <section className="atlas-home-hero-banner atlas-sidebar-card">
        <span className="atlas-eyebrow">{themeConfig.home.eyebrow}</span>
        <h1>{themeConfig.home.title}</h1>
        <p className="atlas-lead">{themeConfig.home.lead}</p>
        <ThemeSwitch className="atlas-theme-switch--hero" />
      </section>

      <section className="atlas-home-theme-strip">
        {themeCountdownCards.map((item) => (
          <article key={item.theme} className={`atlas-home-theme-card atlas-home-theme-card--${item.theme}`}>
            <span className="atlas-card-kicker">{item.themeLabel}</span>
            <h3>{item.title}</h3>
            <p>{item.countdownLabel} · {item.formattedDate}</p>
          </article>
        ))}
      </section>

      <section className="atlas-home-showcase-section">
        <div className="atlas-home-showcase-grid">
          <article className="atlas-home-showcase-map-card atlas-sidebar-card">
            <div className="atlas-home-showcase-map-header">
              <div>
                <p className="atlas-panel-label">Signal globe</p>
                <h3>Watch live and seasonal hotspots settle into an actual world view.</h3>
              </div>
              <span className="atlas-home-map-pill">{briefingLocationLabel}</span>
            </div>
            <SignalGlobe
              entries={showcaseEntries}
              activeIndex={orbitIndex}
              onSelect={setOrbitIndex}
              viewerLocation={{ lat: briefingLat, lon: briefingLng }}
            />
          </article>

          <article className="atlas-home-showcase-copy-card atlas-sidebar-card">
            <div key={activeShowcase.id} className="atlas-home-showcase-copy-pane">
              <p className="atlas-panel-label">Hotspot brief</p>
              <div className="atlas-home-date-flip" aria-live="polite">
                <strong>{activeShowcase.day}</strong>
                <span>{activeShowcase.month}</span>
              </div>
              <span className="atlas-card-kicker">{activeShowcase.themeLabel}</span>
              <h2>{activeShowcase.title}</h2>
              <p className="atlas-card-copy">{activeShowcase.summary}</p>
              <div className="atlas-detail-list">
                <span>
                  <strong>Region in view</strong>
                  {briefingLocationLabel}
                </span>
                <span>
                  <strong>Hotspot</strong>
                  {activeShowcase.regionLabel || activeShowcase.distanceLabel}
                </span>
                <span>
                  <strong>When</strong>
                  {activeShowcase.formattedDate || activeShowcase.detail}
                </span>
                <span>
                  <strong>Next window</strong>
                  {activeShowcase.countdownLabel}
                </span>
              </div>

              <div className="atlas-home-headline-list">
                {showcaseQueue.map((item) => (
                  <div key={item.id} className="atlas-home-headline-item">
                    <strong>{item.title}</strong>
                    <span>{item.themeLabel} · {item.countdownLabel}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="atlas-home-showcase-footer">
              <span className="atlas-home-map-pill">{activeShowcase.distanceLabel}</span>
              <span className="atlas-home-map-pill">{activeShowcase.sourceLabel}</span>
            </div>
          </article>
        </div>
      </section>

      <section className="atlas-home-newsdesk">
        <div className="atlas-home-command">
          <div className="atlas-home-query-card">
            <div className="atlas-home-query-head">
              <div>
                <p className="atlas-panel-label">Region controls</p>
                <h3>Set the coastline, search radius, and time window in view.</h3>
                <p className="atlas-card-copy">
                  These controls drive the local briefing below. Use them to move the globe and the
                  story cards toward the coast, region, or season you actually care about.
                </p>
              </div>
              <button
                type="button"
                className="atlas-secondary-link atlas-control-button"
                onClick={useBrowserLocation}
              >
                Use my location
              </button>
            </div>

            <div className="atlas-control-grid atlas-control-grid--home">
              <label className="atlas-control-label">
                Latitude
                <input
                  className="atlas-control-input"
                  type="number"
                  step="0.01"
                  value={draftLat}
                  onChange={(event) => setDraftLat(event.target.value)}
                />
              </label>
              <label className="atlas-control-label">
                Longitude
                <input
                  className="atlas-control-input"
                  type="number"
                  step="0.01"
                  value={draftLng}
                  onChange={(event) => setDraftLng(event.target.value)}
                />
              </label>
              <label className="atlas-control-label">
                Days ahead
                <select
                  className="atlas-control-input"
                  value={days}
                  onChange={(event) => setDays(Number(event.target.value))}
                >
                  <option value={7}>7</option>
                  <option value={14}>14</option>
                  <option value={30}>30</option>
                </select>
              </label>
              <label className="atlas-control-label">
                Radius km
                <select
                  className="atlas-control-input"
                  value={radiusKm}
                  onChange={(event) => setRadiusKm(Number(event.target.value))}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>

            <div className="atlas-cta-row atlas-cta-row--tight">
              <button
                type="button"
                className="atlas-primary-link atlas-control-button"
                onClick={applyLocationDraft}
              >
                Update briefing
              </button>
              <Link
                className="atlas-secondary-link"
                to={buildThemeHref('/planner', location.search, activeTheme)}
              >
                {themeConfig.home.heroCta}
              </Link>
            </div>

            <div className="atlas-pill-row atlas-pill-row--compact">
              <span className="atlas-pill">Region {briefingLocationLabel}</span>
              <span className="atlas-pill">{days} day window</span>
              <span className="atlas-pill">{radiusKm} km radius</span>
            </div>
            <p className="atlas-home-location-note">{locationNote}</p>
            {error ? <p className="atlas-home-error">{error}</p> : null}
          </div>
        </div>

        <article className="atlas-home-lead atlas-sidebar-card">
          <span className="atlas-card-kicker">Lead story near this region</span>
          <h2>{leadEvent.title}</h2>
          <p className="atlas-card-copy">{leadEvent.summary}</p>
          {loading ? <div className="atlas-empty-state atlas-empty-state--compact">Building local briefing…</div> : null}
          {!loading ? (
            <>
              <div className="atlas-detail-list">
                <span>
                  <strong>Region</strong>
                  {briefingLocationLabel}
                </span>
                <span>
                  <strong>Timing</strong>
                  {formatAtlasDate(leadEvent.starts_at || leadEvent.start_ts)}
                </span>
                <span>
                  <strong>Status</strong>
                  {leadEvent.status || 'scheduled'}
                </span>
              </div>

              <div className="atlas-mini-stats">
                <div>
                  <strong>{eventsPayload?.summary?.event_count ?? 0}</strong>
                  <span>signals</span>
                </div>
                <div>
                  <strong>{eventsPayload?.summary?.active_count ?? 0}</strong>
                  <span>active now</span>
                </div>
                <div>
                  <strong>{countdowns[0]?.countdownLabel || 'TBD'}</strong>
                  <span>next seasonal anchor</span>
                </div>
              </div>
            </>
          ) : null}
          <div className="atlas-cta-row atlas-cta-row--tight">
            <Link className="atlas-primary-link" to={secondaryLeadHref}>
              {themeConfig.home.secondaryCta}
            </Link>
            <Link
              className="atlas-secondary-link"
              to={buildThemeHref('/planner', location.search, activeTheme)}
            >
              {themeConfig.nav.planner}
            </Link>
          </div>
        </article>

        <aside className="atlas-home-rail">
          <article className="atlas-home-brief-card">
            <p className="atlas-panel-label">Use this brief</p>
            <ul className="atlas-bullet-list atlas-bullet-list--compact">
              <li>Check what is active now before you commit to a trip window.</li>
              <li>Use the next-up list to see whether waiting a few days changes the odds.</li>
              <li>Jump into the planner when the date, radius, or subject needs finer control.</li>
            </ul>
          </article>

          <article className="atlas-home-brief-card">
            <p className="atlas-panel-label">Around you next</p>
            <div className="atlas-home-headline-list">
              {headlineItems.map((item) => (
                <div key={item.key} className="atlas-home-headline-item">
                  <strong>{item.title}</strong>
                  <span>{item.meta}</span>
                </div>
              ))}
            </div>
          </article>

          <article className="atlas-home-brief-card">
            <p className="atlas-panel-label">Seasonal anchor</p>
            <h3>{countdowns[0]?.title || 'Seasonal window pending'}</h3>
            <p className="atlas-card-copy">
              {countdowns[0]?.summary || 'Add a location to build the next marker.'}
            </p>
            {countdowns[0] ? (
              <div className="atlas-detail-list">
                <span>
                  <strong>Countdown</strong>
                  {countdowns[0].countdownLabel}
                </span>
                <span>
                  <strong>Date</strong>
                  {countdowns[0].formattedDate}
                </span>
              </div>
            ) : null}
          </article>
        </aside>
      </section>

      <section className="atlas-section">
        <div className="atlas-section-heading">
          <span className="atlas-eyebrow">Around this location</span>
          <h2>Nearby stories and supporting context for the region in view.</h2>
          <p>
            Use these cards to move from the headline signal into the next-best nearby briefs and the
            background that makes those signals easier to interpret.
          </p>
        </div>
        <div className="atlas-home-story-grid">
          <div className="atlas-home-story-column">
            {storyCards.map((item) => (
              <article key={item.key} className="atlas-upcoming-card atlas-upcoming-card--dense">
                <span className="atlas-card-kicker">{item.kicker}</span>
                <h4>{item.title}</h4>
                <p>{item.summary}</p>
                <div className="atlas-detail-list">
                  <span>
                    <strong>Status</strong>
                    {item.status}
                  </span>
                  <span>
                    <strong>When</strong>
                    {formatAtlasDate(item.startsAt)}
                  </span>
                  <span>
                    <strong>Distance</strong>
                    {item.distance != null ? `${item.distance.toFixed(1)} km` : 'regional'}
                  </span>
                </div>
              </article>
            ))}
          </div>

          <div className="atlas-home-context-column">
            {contextCards.map((item) => (
              <article key={item.key} className="atlas-upcoming-card">
                <span className="atlas-card-kicker">{item.kicker}</span>
                <h4>{item.title}</h4>
                <p>{item.summary}</p>
                {item.details.length ? (
                  <div className="atlas-detail-list">
                    {item.details.map((detail) => (
                      <span key={`${item.key}-${detail.label}`}>
                        <strong>{detail.label}</strong>
                        {detail.value}
                      </span>
                    ))}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="atlas-section">
        <div className="atlas-section-heading">
          <span className="atlas-eyebrow">Countdown desk</span>
          <h2>Keep the recurring annual windows visible for fast orientation.</h2>
          <p>
            These markers keep the year legible at a glance so you can tell whether this month is
            worth watching more closely before you open the deeper pages.
          </p>
        </div>
        <div className="atlas-home-countdown-grid">
          {countdowns.map((item) => (
            <article key={item.id} className="atlas-home-countdown-card">
              <span className="atlas-card-kicker">{item.subtitle}</span>
              <h3>{item.title}</h3>
              <p>{item.summary}</p>
              <div className="atlas-home-countdown-meta">
                <strong>{item.countdownLabel}</strong>
                <span>{item.formattedDate}</span>
              </div>
              <p className="atlas-home-countdown-note">{item.cadence}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="atlas-section">
        <div className="atlas-section-heading">
          <span className="atlas-eyebrow">Deeper desks</span>
          <h2>Keep moving with the focused pages for this theme.</h2>
        </div>
        <div className="atlas-track-grid atlas-track-grid--four">
          {featuredTracks.map((track) => (
            <article key={track.title} className="atlas-track-card">
              <h3>{track.title}</h3>
              <p>{track.description}</p>
              <Link to={track.href}>Open {track.title.toLowerCase()}</Link>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};
